import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as productApi from '../api/productApi';

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchProducts(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch products');
    }
  }
);

export const fetchProductBySlug = createAsyncThunk(
  'products/fetchProductBySlug',
  async (slug, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchProductBySlug(slug);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Product not found');
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeatured',
  async (_, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchFeaturedProducts();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch featured products');
    }
  }
);

export const fetchNewArrivals = createAsyncThunk(
  'products/fetchNewArrivals',
  async (_, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchNewArrivals();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch new arrivals');
    }
  }
);

export const fetchBestSellers = createAsyncThunk(
  'products/fetchBestSellers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchBestSellers();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch best sellers');
    }
  }
);

export const fetchProductsByCategory = createAsyncThunk(
  'products/fetchByCategory',
  async ({ slug, params }, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchProductsByCategory(slug, params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch category products');
    }
  }
);

export const fetchRelatedProducts = createAsyncThunk(
  'products/fetchRelated',
  async (slug, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchRelatedProducts(slug);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch related products');
    }
  }
);

export const searchProducts = createAsyncThunk(
  'products/search',
  async (params, { rejectWithValue }) => {
    try {
      const data = await productApi.searchProducts(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Search failed');
    }
  }
);

export const fetchAutocomplete = createAsyncThunk(
  'products/autocomplete',
  async (query, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchAutocomplete(query);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Autocomplete failed');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const data = await productApi.fetchCategories();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch categories');
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState: {
    products: [],
    featuredProducts: [],
    newArrivals: [],
    bestSellers: [],
    currentProduct: null,
    categories: [],
    relatedProducts: [],
    searchResults: [],
    autocompleteSuggestions: { products: [], categories: [] },
    loading: false,
    error: null,
    pagination: {
      page: 1,
      pages: 1,
      total: 0,
      limit: 12,
    },
  },
  reducers: {
    clearProductError: (state) => {
      state.error = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearAutocomplete: (state) => {
      state.autocompleteSuggestions = { products: [], categories: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products || action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProductBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload.product || action.payload.data || action.payload;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.featuredProducts = action.payload.products || action.payload.data || action.payload || [];
      })
      .addCase(fetchNewArrivals.fulfilled, (state, action) => {
        state.newArrivals = action.payload.products || action.payload.data || action.payload || [];
      })
      .addCase(fetchBestSellers.fulfilled, (state, action) => {
        state.bestSellers = action.payload.products || action.payload.data || action.payload || [];
      })
      .addCase(fetchProductsByCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products || action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.relatedProducts = action.payload.products || action.payload.data || action.payload || [];
      })
      .addCase(searchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.loading = false;
        // /products/search seedha products ka array bhejta hai (envelope pehle
        // productApi me unwrap ho chuka hota hai), jabki /search object bhejta hai.
        // Dono shape handle karo warna search hamesha 0 results dikhata hai.
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.searchResults = payload;
          state.pagination = { page: 1, limit: payload.length, total: payload.length, pages: 1 };
        } else {
          state.searchResults = payload?.products || payload?.data || [];
          if (payload?.pagination) {
            state.pagination = payload.pagination;
          }
        }
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAutocomplete.fulfilled, (state, action) => {
        const payload = action.payload;
        state.autocompleteSuggestions = Array.isArray(payload)
          ? { products: payload, categories: [] }
          : {
              products: payload.products || [],
              categories: payload.categories || [],
            };
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.categories || action.payload.data || action.payload || [];
      });
  },
});

export const { clearProductError, clearCurrentProduct, clearSearchResults, clearAutocomplete } = productSlice.actions;
export default productSlice.reducer;
